import React from 'react';
import { Eye, Heart, MessageSquare } from 'lucide-react';

interface SearchResultsViewProps {
  searchQuery: string;
  totalSearchResults: number;
  paginatedResults: any[];
  searchPage: number;
  ITEMS_PER_PAGE: number;
  loadedSearchImages: { [key: string]: boolean };
  setLoadedSearchImages: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  onSelectVideo: (id: string) => void;
  onPageChange: (newPage: number) => void;
}

export default function SearchResultsView({
  searchQuery,
  totalSearchResults,
  paginatedResults,
  searchPage,
  ITEMS_PER_PAGE,
  loadedSearchImages,
  setLoadedSearchImages,
  onSelectVideo,
  onPageChange,
}: SearchResultsViewProps) {
  const formatCount = (num: number) => {
    if (!num) return '0';
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace('.', ',') + ' mil';
    }
    return num.toString();
  };

  return (
    <div style={{ width: '100%', padding: '20px 0' }}>
      {/* Título com leve recuo lateral */}
      <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff', marginBottom: '20px', padding: '0 12px' }}>
        Resultados para: "{searchQuery}" ({totalSearchResults} encontrados)
      </h2>

      {/* Grid dos itens paginados */}
      {paginatedResults.length > 0 ? (
        <div style={{ width: '100%', padding: '0 8px' }}>
          <style>{`
            @keyframes shimmer {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
            .image-skeleton {
              background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
              background-size: 200% 100%;
              animation: shimmer 1.5s infinite;
            }
            
            /* 📱 PADRÃO DE TELEMÓVEL: Exatamente 2 colunas */
            .search-results-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
              width: 100%;
            }

            /* 💻 PADRÃO DE COMPUTADOR (768px ou mais): Força rigorosamente 5 colunas */
            @media (min-width: 768px) {
              .search-results-grid {
                grid-template-columns: repeat(5, 1fr) !important;
                gap: 16px !important;
                max-width: 1400px;
                margin: 0 auto;
                padding: 0 10px;
              }
            }
          `}</style>

          <div className="search-results-grid">
            {paginatedResults.map((video: any) => {
              const isLoaded = Boolean(loadedSearchImages[video.id]);

              return (
                <div 
                  key={video.id}
                  onClick={() => {
                    const isShortVideo = video.post_type === 'shorts' || video.is_short === true;
                    const isFeedVideo = video.post_type === 'feed' || video.post_type === 'app_movel';

                    if (isShortVideo) {
                      window.location.href = `/shorts?v=${video.id}`;
                    } else if (isFeedVideo) {
                      // 👉 AQUI ESTÁ A CORREÇÃO: mudamos de /feed para /playfeed
                      window.location.href = `/playfeed?v=${video.id}`; 
                    } else if (onSelectVideo) {
                      onSelectVideo(video.id);
                    }
                  }}
                  style={{ cursor: 'pointer', backgroundColor: '#141414', borderRadius: '6px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div style={{ width: '100%', aspectRatio: '16/9', position: 'relative', backgroundColor: '#111' }}>
                    {!isLoaded && (
                      <div className="image-skeleton" style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
                    )}
                    <img 
                      src={video.thumbnail} 
                      alt={video.title} 
                      loading="lazy"
                      decoding="async"
                      onLoad={() => setLoadedSearchImages(prev => ({ ...prev, [video.id]: true }))}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        opacity: isLoaded ? 1 : 0,
                        transition: 'opacity 0.3s ease-in-out',
                        position: 'relative',
                        zIndex: 2
                      }} 
                    />

                    {/* 🟢 ETIQUETA NA CAPA (Esconde se for Short) */}
                    {!(video.post_type === 'shorts' || video.is_short === true || (video.category || '').toLowerCase().includes('shorts')) && (
                      <div style={{ 
                        position: 'absolute', 
                        top: '6px', 
                        right: '6px', 
                        backgroundColor: video.category?.toLowerCase().includes('trailer') ? 'rgba(229, 9, 20, 0.9)' : 'rgba(0, 0, 0, 0.85)', 
                        color: '#fff', 
                        fontSize: '8px', 
                        fontWeight: 'bold', 
                        padding: '2px 5px', 
                        borderRadius: '3px', 
                        zIndex: 4,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        border: '1px solid rgba(255,255,255,0.15)'
                      }}>
                        {video.category?.toLowerCase().includes('trailer') 
                          ? '🎬 Trailer' 
                          : video.category?.toLowerCase().includes('game') 
                            ? '🎮 Game' 
                            : '🎥 Filme'}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ padding: '10px', minHeight: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '600', margin: '0 0 4px 0', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {video.title}
                    </h4>
                    
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#888', fontSize: '11px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <Eye size={12} /> {formatCount(video.views_count ?? 0)}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <Heart size={12} color="#E50914" /> {formatCount(video.likes_count ?? 0)}
                      </span>
                      {Number(video.comments_count) > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <MessageSquare size={12} color="#3b82f6" /> {formatCount(video.comments_count)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Barra de Paginação Real */}
          {totalSearchResults > ITEMS_PER_PAGE && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginTop: '40px' }}>
              <button
                disabled={searchPage === 1}
                onClick={() => onPageChange(searchPage - 1)}
                style={{
                  backgroundColor: searchPage === 1 ? '#222' : '#E50914',
                  color: searchPage === 1 ? '#555' : '#fff', border: 'none', padding: '10px 20px',
                  borderRadius: '4px', cursor: searchPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 'bold'
                }}
              >
                ⬅️ Anterior
              </button>

              <span style={{ color: '#fff', fontSize: '14px' }}>
                Página {searchPage} de {Math.ceil(totalSearchResults / ITEMS_PER_PAGE)}
              </span>

              <button
                disabled={searchPage * ITEMS_PER_PAGE >= totalSearchResults}
                onClick={() => onPageChange(searchPage + 1)}
                style={{
                  backgroundColor: searchPage * ITEMS_PER_PAGE >= totalSearchResults ? '#222' : '#E50914',
                  color: searchPage * ITEMS_PER_PAGE >= totalSearchResults ? '#555' : '#fff', border: 'none', padding: '10px 20px',
                  borderRadius: '4px', cursor: searchPage * ITEMS_PER_PAGE >= totalSearchResults ? 'not-allowed' : 'pointer', fontWeight: 'bold'
                }}
              >
                Próxima ➡️
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>
          Nenhum vídeo encontrado para "{searchQuery}".
        </div>
      )}
    </div>
  );
}